// Синхронный детектор видимости приложения для presence-aware подавления
// уведомлений (см. docs/specs/presence-aware-notifications.md §3.3).
//
// Вызывается ВНУТРИ обработчиков событий (toast-listener, страницы-свидетели),
// НЕ в рендере — поэтому хук не подходит, нужен синхронный `isAppVisible()`.
//
// Web: document.visibilityState читается синхронно.
// Capacitor: WebView document.visibilityState на background ненадёжен → держим
//   собственный кэш, обновляемый через платформенный порт app-state
//   (onForeground/onBackground поверх @capacitor/app appStateChange).

import { appStateCapacitorAdapter } from "@studyhub/platform/adapters/capacitor/app-state.capacitor";
import { IS_CAPACITOR } from "@/lib/platform-detect";

let capacitorVisible = true;
let initialized = false;
const visibilityListeners = new Set<() => void>();

function notifyVisibilityListeners(): void {
	for (const listener of visibilityListeners) listener();
}

/**
 * Однократно подписывается на app-state в Capacitor / visibilitychange на web,
 * чтобы `isAppVisible()` и `onAppVisibilityChange` были актуальны.
 *
 * Безопасно вызывать многократно — повторные вызовы игнорируются.
 */
export function initAppVisibleTracking(): void {
	if (initialized) return;
	initialized = true;
	if (IS_CAPACITOR) {
		appStateCapacitorAdapter.onForeground(() => {
			capacitorVisible = true;
			notifyVisibilityListeners();
		});
		appStateCapacitorAdapter.onBackground(() => {
			capacitorVisible = false;
			notifyVisibilityListeners();
		});
		return;
	}
	if (typeof document === "undefined") return;
	document.addEventListener("visibilitychange", () => notifyVisibilityListeners());
}

/**
 * Подписка на смену видимости (web visibilitychange / Capacitor appState).
 * Гарантирует `initAppVisibleTracking()`.
 */
export function onAppVisibilityChange(handler: () => void): () => void {
	initAppVisibleTracking();
	visibilityListeners.add(handler);
	return () => {
		visibilityListeners.delete(handler);
	};
}

/**
 * Видимо ли приложение прямо сейчас (синхронно).
 *  - Capacitor → кэш из appStateChange (foreground = true).
 *  - Web → document.visibilityState === "visible".
 */
export function isAppVisible(): boolean {
	if (IS_CAPACITOR) return capacitorVisible;
	return typeof document !== "undefined" && document.visibilityState === "visible";
}
